import * as React from 'react';
import { observable, action } from 'mobx';
import { IDraft } from '../../../interfaces/IDraft';
import { ICheckOut } from '../../../interfaces/ICheckOut';
import { IUserRole } from '../../../interfaces/IUserRole';
import { IUser } from '../../../interfaces/IUser';
import { INavigator } from '../../../interfaces/INavigator';
import { Property } from 'csstype';
import { Helper } from '../../../Helper';
// import { TopItemDroppable } from '../megamenu/topItems/topItem/TopItemDroppable';
import { TopItem } from '../megamenu/topItems/topItem/TopItem';
import { IAIcon } from '../../icon/Icon';
import * as megamenuStyles from '../megamenu/Megamenu.css';
import { IConfig } from '../../../interfaces/IConfig';
import NavigatorService from '../../../services/NavigatorService';
import { Environment } from '../../../services/Environment';
import { IHttpStatus } from '../../../interfaces/IHttpStatus';
import { ITargetingApplications } from '../../../interfaces/ITargetingApplications';
import { TargetingService } from '../../../services/TargetingService';
import { IApplication, IDefaultValues, Instance } from '../../../interfaces/IApplication';
import { ITenantSettings } from '../../../interfaces/ITenantSettings';
import { LocalizationService } from '../../../services/LocalizationService';
import { IComponentInstances } from '../../../interfaces/IComponentInstances';
import { INavigatorClickDictionary } from '../../../interfaces/INavigatorClickDictionary';
import DateTimeService from '../../../services/DateTimeService';
import AnalyticsService from '../../../services/AnalyticsService';
import * as Constants from "../Constants";
import { IFontSlots } from '../../../interfaces/IFontSlots';
import { ITopItem } from '../../../interfaces/ITopItem';

export class NavigatorStore {
  @observable public id: string;

  @observable public navigator: INavigator;
  private localizationService: LocalizationService = new LocalizationService();
  private readonly dateTimeService: DateTimeService = new DateTimeService();

  @observable public isClassic: boolean;
  @observable public navigatorAddedToContainer: boolean;

  @observable public environment: 'Development' | 'Test' | 'Production';
  @observable public width: number;
  @observable public tenant: string;
  @observable public token: string;
  @observable public instance: string;
  @observable public instanceId: number;
  @observable public isInEditMode: boolean = false;
  @observable public hasChanged: string;
  @observable public isLoading: boolean;
  @observable public isSaving: boolean;
  @observable public isPublishing: boolean;
  @observable public isMobile: boolean = false;
  @observable public isIpadInPortrait: boolean = false;
  @observable public navigatorTop: number;
  @observable public contentHeight: number;
  @observable public contentTop: number;
  @observable public applicationSettings: ITargetingApplications;
  @observable public tenantSettings: ITenantSettings;
  @observable public highlightColor: string;
  @observable public loadLinksExternally: boolean;
  @observable public asMobileAppComponent: boolean;
  @observable public isPreview: boolean;
  @observable public isAdminPage: boolean;
  @observable public isShowingAnalytics: boolean;
  @observable public getPageInformation: () => Promise<{ pageTitle: string, topicHeader: string, textControls: string[] }>;
  @observable public fontSlots: IFontSlots;

  //Analytics
  @observable public fromDate: Date = new Date(new Date().setMonth(new Date().getMonth() - 1)); //Default to 1 month prior to current date
  @observable public toDate: Date = new Date();
  @observable public source: "total" | "web" | "mobile" | "android" | "ios" = "total";
  @observable public isDownloading: boolean;
  @observable public isFetchingClicks: boolean;
  @observable public downloadFailed: boolean;
  @observable public navigatorClicks: INavigatorClickDictionary;
  @observable public showClicks: boolean;
  @observable public leastClickedAmount: number = Number.POSITIVE_INFINITY;
  @observable public mostClickedAmount: number = Number.NEGATIVE_INFINITY;
  @observable public leastUniqueClickedAmount: number = Number.POSITIVE_INFINITY;
  @observable public mostUniqueClickedAmount: number = Number.NEGATIVE_INFINITY;
  @observable public totalClicks: number = 0;
  @observable public totalUniqueClicks: number = 0;
  @observable public doneSettingClicks: boolean = false;
  @observable public visualizationFailed: boolean = false;
  @observable public areThereClicksInPeriod: boolean = true;
  @observable public clickFilter: 'Total' | 'Unique' = 'Total';
  @observable public logoWidth: number;
  @observable public logoHeight: number;

  // Version
  @observable public versions: IDraft[];
  @observable public isShowingNewVersionPopup: boolean;
  @observable public newVersionShouldBePublic: boolean;
  @observable public checkedOut: boolean;
  @observable public checkOut: ICheckOut;

  // Design settings
  @observable public isShowingDesignSettings: boolean;

  @observable public navigatorContainer: any;

  // Admin
  @observable public adminColor: string = '#23879b';
  @observable public adminPivot: string = 'Content';
  @observable public isCreatingContent: boolean;

  // Top item
  @observable public topItems: JSX.Element[];
  @observable public selectedTopMenuItemIndex: number;
  @observable public isShowingTopItemSettings: boolean;
  @observable public topItemIndexToEdit: number;
  @observable public droppableTopItemSpotsAreShowing: boolean = false;
  @observable public draggedTopItemIndex: number;
  @observable public showMoreHorizontalTopItems: boolean;
  @observable public showMoreHorizontalTopItemsButton: boolean;
  @observable public horizontalTopItemsToDisplay: JSX.Element[];
  @observable public moreHorizontalTopItemsToDisplay: JSX.Element[];

  // Element groups
  @observable public isShowingElementGroupSettings: boolean;
  @observable public elementGroupColumnIndexToEdit: number;
  @observable public elementGroupIndexToEdit: number;
  @observable public droppableElementGroupSpotsAreShowing: boolean = false;
  @observable public draggedColumnIndex: number;
  @observable public draggedElementGroupIndex: number;

  // Element
  @observable public isShowingElementSettings: boolean;
  @observable public elementIndexToEdit: number;
  @observable public droppableElementSpotsAreShowing: boolean = false;
  @observable public draggedElementIndex: number;

  // Megamenu
  @observable public navigatorHeight: number;
  @observable public isShowingMegamenu: boolean = false;
  @observable public horizontalMenuWidth: number;
  @observable public horizontalMenuLeft: number;
  @observable public verticalMenuHeight: number;
  @observable public verticalMenuExpanded: boolean;
  @observable public smartmenuWidth: number;
  @observable public smartmenuHeight: number;
  @observable public numberOfElementsInSmartmenu: number;
  @observable public showEditButton: boolean;

  // Smartmenu
  @observable public isShowingAdditionalSmartmenuItems: boolean;
  @observable public droppableSmartmentTopItemSpotsAreShowing: boolean;
  @observable public smartmenuTopItemIndexToEdit: number;
  @observable public showMoreSmartmenuTopItemsButton: boolean = false;

  // Phonebook
  @observable public selectedPhonebook: IConfig;

  // People Finder
  @observable public selectedPeopleFinder: IConfig;

  // Event
  @observable public selectedEvent: IConfig;

  // Content Creator
  @observable public selectedContentCreator: IConfig;

  // AI assistent
  @observable public selectedAiAssistent: IConfig;

  // Translation
  @observable public selectedTranslationPage: boolean;
  @observable public isNavigatedEventRegistered: boolean;
  @observable public translationIconRight: number;
  @observable public isTranslating: boolean;
  // Error
  @observable public httpStatus: IHttpStatus;
  @observable public httpStatusMarginTop: number;

  // Instamces
  @observable public instances: any[];
  @observable public componentInstances: IComponentInstances = { Messages: [], Event: [] };

  // Teams
  @observable public inTeams: boolean;

  @observable public showForgotToSavePopup: boolean;

  // Targeted Preview
  @observable public targetedPreviewIds: number[] = [];
  @observable public showTargetPreviewPopup: boolean;
  @observable public showTargetPreviewPanelWithTitle: string;
  @observable public targetingGroups: any[];

  @action public getTargeting(): void {
    TargetingService.getGroups(this.environment, this.tenant.replace(".sharepoint.com", ""), this.token,).then((groups) => {
      this.targetingGroups = groups;
    });
  }

  @action public getSelectedValues(): any[] {
    const selectedValues = [];
    if (this.targetingGroups?.length > 0 && this.targetedPreviewIds?.length > 0) {
      this.targetingGroups.forEach((group: any) => {
        this.targetedPreviewIds.forEach((id: number) => {
          if (`${group?.id}` === `${id}`) {
            selectedValues.push({
              Label: group.title,
              TermGuid: group.title,
              id: group.id,
              Name: group.title
            });
          }
        })
      })
    }
    return selectedValues;
  }

  @action public checktargetedPreviewIds(element): boolean {
    let isPartOftargetedPreviewIds = false;
    if (this.targetedPreviewIds && this.targetedPreviewIds.length > 0 && element?.targeting?.enabled && element?.targeting?.targetingGroups?.length > 0) {
      element?.targeting?.targetingGroups.forEach((targetingGroup: string) => {
        this.targetedPreviewIds.forEach((targetedPreviewId: number) => {
          if (`${targetingGroup}` === `${targetedPreviewId}`) {
            isPartOftargetedPreviewIds = true;
          }
        });
      })
    } else {
      isPartOftargetedPreviewIds = true;
    }
    return isPartOftargetedPreviewIds;
  }

  // Configuration
  @action public getNavigatorFromCache(): any {
    if (localStorage.getItem(`navigator-${this.instanceId}`) != undefined) {
      this.navigator = JSON.parse(localStorage.getItem(`navigator-${this.instanceId}`));
      this.checkSize();
      if (this.isMobile || (this.isClassic && this.navigator.design.menuBar.layout == 'vertical')) {
        this.navigator.design.menuBar.layout = 'burger';
      }
      this.getContainer();
      this.presentNavigator();
    }
  }

  @action public setNavigator(navigator: any): any {
    if (this.asMobileAppComponent) {
      this.isMobile = true;
    }

    if (navigator.smartmenu == null) {
      navigator.smartmenu = {
        elements: [],
      };
    }
    if (navigator?.smartmenu?.elements?.length > 0) {
      //navigator.smartmenu.elements = navigator.smartmenu.elements.filter(element => this.checktargetedPreviewIds(element) || (element.type === "targetedpreview" && this.targetedPreviewIds?.length > 0));
      navigator.smartmenu.elements.forEach((element: ITopItem) => {
        if (!element.config.id) {
          element.config.id = Helper.generateGUID();
        }
      })
    }
    this.hasChanged = undefined;
    this.checkSize();
    if (this.isMobile || (this.isClassic && navigator.design.menuBar.layout == 'vertical')) {
      navigator.design.menuBar.layout = 'burger';
    }
    let needReload = false;
    if (
      document.getElementById('NavigatorContainer') == undefined &&
      document.getElementsByClassName('spAppAndPropertyPanelContainer')[0] == undefined &&
      document.getElementsByClassName('sp-App-body')[0] == undefined
    ) {
      navigator.design.menuBar.layout = 'horizontal';
    } else {
      if (
        this.navigator != undefined &&
        this.navigator.design.menuBar.layout != navigator.design.menuBar.layout &&
        !this.isInEditMode
      ) {
        if (navigator.design.menuBar.layout != 'burger' && !this.isMobile) {
          needReload = true;
        }
      }
    }
    this.navigator = navigator;
    Helper.setLocalStorage(`navigator-${this.instanceId}`, JSON.stringify(navigator));
    this.getContainer();
    setTimeout(() => {
      if (needReload) {
        location.reload();
      } else {
        this.resize();
        this.presentNavigator();
      }
    }, 50);
  }

  @action private checkSize() {
    if (this.navigatorContainer == undefined) {
      if (this.isInEditMode) {
        this.navigatorContainer = document.getElementById('NavigatorContainer');
      } else {
        if (document.getElementsByClassName('spAppAndPropertyPanelContainer')[0] != undefined && document.getElementById('NavigatorContainerMobileApp') == undefined) {
          this.navigatorContainer = document.getElementsByClassName('spAppAndPropertyPanelContainer')[0];
        }
        if (document.getElementsByClassName('sp-App-body')[0] != undefined && document.getElementById('NavigatorContainerMobileApp') == undefined) {
          this.navigatorContainer = document.getElementsByClassName('sp-App-body')[0];
        }
        if (document.getElementById('NavigatorContainerMobileApp') != undefined) {
          this.navigatorContainer = document.getElementById('NavigatorContainerMobileApp');
        }
      }
      if (document.getElementById('s4-workspace') != undefined) {
        this.navigatorContainer = document.getElementById('s4-workspace');
        this.isClassic = true;
      }
    }
    this.width = this.navigatorContainer != undefined ? this.navigatorContainer.offsetWidth : 0;
    if (document.getElementById("sp-appBar") && document.getElementById("spPageChromeAppDiv") && document.getElementById("spPageChromeAppDiv").offsetWidth < window.innerWidth) {
      this.width = this.width - 47;
    }
    if (this.isPreview) {
      this.width = 350;
      this.isMobile = true;
    }
    if (!this.isMobile && this.navigator != undefined && this.width !== 0) {
      this.isMobile =
        this.navigator.design.menuBar.layout == 'vertical' && document.getElementById('IA_Navigator') == undefined
          ? this.width < 717 && !this.isInEditMode
          : this.width < 667 && !this.isInEditMode;
      this.isIpadInPortrait =
        this.navigator.design.menuBar.layout == 'vertical' && document.getElementById('IA_Navigator') == undefined
          ? this.width >= 717 && this.width < 1074 && !this.isInEditMode
          : this.width >= 667 && this.width < 1024 && !this.isInEditMode;
    }
  }

  @action private getContainer(): void {
    if (this.navigatorContainer != undefined && document.getElementById('IA_Navigator') != undefined) {
      const displayEnvironment = Helper.getDisplayEnvironment();
      this.width = this.navigatorContainer.offsetWidth;
      if (document.getElementById("sp-appBar") && document.getElementById("spPageChromeAppDiv") && document.getElementById("spPageChromeAppDiv").offsetWidth < window.innerWidth) {
        this.width = this.width - 47;
      }
      if (this.isPreview) {
        this.width = 350;
        this.isMobile = true;
      }
      let SuiteNavPlaceHolder: any = document.getElementById('SuiteNavPlaceholder');
      if (!this.inTeams && displayEnvironment === "Sharepoint") {
        SuiteNavPlaceHolder.style.zIndex = '595 !important';
      }
      if ((this.navigator.design.menuBar.layout == 'vertical' && !this.isClassic) || this.isInEditMode) {
        this.navigatorContainer.style.position = 'absolute';
        this.navigatorContainer.style.width = this.isInEditMode ? 'calc(100% - 40px)' : 'calc(100% - 48px)';
        if (this.navigator.design.menuBar.layout == 'vertical' && !this.isInEditMode) {
          if (!this.inTeams && (displayEnvironment === "Sharepoint" || displayEnvironment === "SharepointClassic")) {
            this.navigatorContainer.style.height = 'calc(100% - 48px)';
          }
        }
        if (this.navigator.design.menuBar.layout == 'vertical' && this.isInEditMode) {
          this.navigatorContainer.style.width = 'calc(100% - 88px)';
        }
        let top = '0px';
        if (!this.inTeams && (displayEnvironment === "Sharepoint" || displayEnvironment === "SharepointClassic")) {
          top = '48px';
        }
        this.navigatorContainer.style.top = top;
        this.navigatorContainer.appendChild(document.getElementById('IA_Navigator'));
        this.navigatorAddedToContainer = true;
      }
      if (!this.isInEditMode) {
        this.navigatorContainer.style.transition = 'all 200ms ease-in-out';
        this.navigatorAddedToContainer = true;
      }
    } else {
      setTimeout(() => {
        this.getContainer();
      }, 100);
    }
  }

  @action private presentNavigator(): void {
    if (this.navigator != undefined && this.isMobile) {
      this.navigator.design.menuBar.layout = 'burger';
    }
    if (this.navigator != undefined && this.navigator.design.menuBar.layout == 'vertical') {
      setTimeout(() => {
        if (this.navigatorContainer != undefined) {
          if (this.navigatorContainer.style.left != '50px') {
            this.navigatorContainer.style.left = '0px';
          }
        } else {
          setTimeout(() => {
            this.getContainer();
            this.presentNavigator();
          }, 100);
        }
      }, 0);
      setTimeout(() => {
        if (this.navigatorContainer != undefined) {
          this.navigatorContainer.style.left = this.isInEditMode ? '70px' : '50px';
        }
      }, 50);
    } else {
      setTimeout(() => {
        if (this.navigatorContainer != undefined) {
          this.navigatorContainer.style.left = this.isInEditMode ? '20px' : '0px';
        } else {
          setTimeout(() => {
            this.getContainer();
            this.presentNavigator();
          }, 100);
        }
      }, 0);
    }
  }

  @action public resize(): void {
    this.checkSize();
    const displayEnvironment = Helper.getDisplayEnvironment();
    if (this.navigator != undefined) {
      let navigator: any = document.getElementById('IA_Navigator');
      if (navigator != null && this.navigatorContainer != null) {
        this.width = this.navigatorContainer.offsetWidth;
        if (document.getElementById("sp-appBar") && document.getElementById("spPageChromeAppDiv") && document.getElementById("spPageChromeAppDiv").offsetWidth < window.innerWidth) {
          this.width = this.width - 47;
        }
        if (
          this.isShowingTopItemSettings ||
          this.isShowingElementGroupSettings ||
          this.isShowingElementSettings ||
          this.isShowingDesignSettings ||
          this.isShowingAnalytics
        ) {
          this.width -= 400;
        }
      }
      this.navigatorTop = 0;
      this.navigatorHeight = 40;
      if (this.navigator.design.menuBar.layout == 'vertical') {
        if (displayEnvironment === "Sharepoint") {
          this.navigatorHeight = this.isInEditMode ? window.innerHeight - 178 : window.innerHeight - 48;
        } else if (displayEnvironment === "SharepointClassic") {
          this.navigatorHeight = this.isInEditMode ? window.innerHeight - 178 : window.innerHeight - 48;
        } else {
          this.navigatorHeight = this.navigatorContainer.offsetHeight;
        }
      }

      // Logo width
      let logo: any = document.getElementById('IA_navigator_logo');
      let logoWidth = logo != undefined ? logo.offsetWidth + 32 : 0;

      //Logo width and height for overlay on analytics
      this.logoWidth = logo != undefined ? logo.offsetWidth : 0;
      this.logoHeight = logo != undefined ? logo.offsetHeight : 0;

      // Smartmenu
      let MAX_SMARTMENU_ITEMS_TO_DISPLAY =
        this.navigator.design.menuBar.numberOfVisibleSmartmenuItems != undefined
          ? this.navigator.design.menuBar.numberOfVisibleSmartmenuItems
          : 5;
      MAX_SMARTMENU_ITEMS_TO_DISPLAY = this.isMobile && MAX_SMARTMENU_ITEMS_TO_DISPLAY > 3 ? 3 : MAX_SMARTMENU_ITEMS_TO_DISPLAY; // Max 3 items on mobile
      if (this.navigator.smartmenu != undefined) {
        this.numberOfElementsInSmartmenu =
          this.navigator.smartmenu.elements.length < MAX_SMARTMENU_ITEMS_TO_DISPLAY
            ? this.navigator.smartmenu.elements.length
            : MAX_SMARTMENU_ITEMS_TO_DISPLAY;
        this.smartmenuWidth =
          this.navigator.design.menuBar.layout == 'vertical' ? 50 : (this.isMobile && this.numberOfElementsInSmartmenu > MAX_SMARTMENU_ITEMS_TO_DISPLAY ? MAX_SMARTMENU_ITEMS_TO_DISPLAY : this.numberOfElementsInSmartmenu) * 40;
        this.smartmenuHeight =
          this.navigator.design.menuBar.layout == 'vertical' ? (this.isMobile && this.numberOfElementsInSmartmenu > MAX_SMARTMENU_ITEMS_TO_DISPLAY ? MAX_SMARTMENU_ITEMS_TO_DISPLAY : this.numberOfElementsInSmartmenu) * 50 + 20 : 40;
        if (this.navigator.smartmenu.elements.length > MAX_SMARTMENU_ITEMS_TO_DISPLAY || (this.isMobile && this.navigator.smartmenu.elements.length > MAX_SMARTMENU_ITEMS_TO_DISPLAY) || (this.isInEditMode && this.navigator.smartmenu.elements.length > 0)
        ) {
          this.showMoreSmartmenuTopItemsButton = true;
          if (this.navigator.design.menuBar.layout == 'vertical') {
            this.smartmenuHeight += 50;
          } else {
            this.smartmenuWidth += 40;
          }
        } else if (
          this.isInEditMode &&
          this.navigator.smartmenu.elements.length == 0 &&
          this.navigator.design.menuBar.layout != 'vertical'
        ) {
          this.smartmenuWidth = 100;
        } else if (
          this.isInEditMode &&
          this.navigator.smartmenu.elements.length == 0 &&
          this.navigator.design.menuBar.layout == 'vertical'
        ) {
          this.smartmenuHeight = 60;
        }
        if (this.navigator.design.menuBar.layout == 'vertical' && this.isShowingAdditionalSmartmenuItems) {
          this.smartmenuHeight = this.navigator.smartmenu.elements.length * 50 + 20 + 50;
          if (this.isInEditMode) {
            if (this.navigator.smartmenu.elements.length > 1) {
              this.smartmenuHeight += 80;
            } else {
              this.smartmenuHeight += 40;
            }
          }
        } else if (this.navigator.design.menuBar.layout != 'vertical' && this.isShowingAdditionalSmartmenuItems) {
          this.smartmenuWidth = this.width;
        }
        if (this.navigator.design.menuBar.layout == 'vertical' && this.droppableTopItemSpotsAreShowing) {
          this.smartmenuHeight = 0;
        }
      } else {
        if (this.isInEditMode && this.navigator.design.menuBar.layout != 'vertical') {
          this.smartmenuWidth = 100;
        }
      }

      // Horizontal bar
      if (this.navigator.design.menuBar.layout == 'horizontal') {
        this.horizontalTopItemsToDisplay = undefined;
        this.moreHorizontalTopItemsToDisplay = undefined;
        this.horizontalMenuWidth = !this.isShowingAdditionalSmartmenuItems
          ? this.width - logoWidth - this.smartmenuWidth - 50
          : 0;
        this.horizontalMenuLeft = logoWidth;
        let hiddenTopItemsWidth = 0;
        if (this.showMoreHorizontalTopItems) {
          this.showMoreHorizontalTopItemsButton = false;
          let numberOfHorizontalTopItems = 0;
          let parentDiv = document.getElementById('hiddenHorizontalMenu');
          let visibleTopItemsWidth = 0;
          if (parentDiv != undefined) {
            // Visible top items
            for (let i = 0; i < parentDiv.childElementCount; i++) {
              let addedItem: any = parentDiv.children[i];
              let style = window.getComputedStyle(addedItem);
              visibleTopItemsWidth += addedItem.offsetWidth + parseFloat(style.marginLeft) + parseFloat(style.marginRight);
              if (visibleTopItemsWidth < this.horizontalMenuWidth) {
                numberOfHorizontalTopItems++;
              } else {
                hiddenTopItemsWidth += addedItem.offsetWidth + parseFloat(style.marginLeft) + parseFloat(style.marginRight);
              }
            }
            this.horizontalTopItemsToDisplay = this.topItems.slice(0, numberOfHorizontalTopItems);
            this.moreHorizontalTopItemsToDisplay = this.topItems.slice(
              numberOfHorizontalTopItems,
              this.topItems.length,
            );
            setTimeout(() => {
              if (hiddenTopItemsWidth > 0) {
                this.navigatorHeight = 40 + document.getElementById('moreHorizontalMenu').offsetHeight;
              } else {
                this.navigatorHeight = 40;
              }
            }, 0);
          }
        } else {
          let numberOfHorizontalTopItems = 0;
          let parentDiv = document.getElementById('hiddenHorizontalMenu');
          let tempWidth = 0;
          if (parentDiv != undefined) {
            for (let i = 0; i < parentDiv.childElementCount; i++) {
              let addedItem: any = parentDiv.children[i];
              let style = window.getComputedStyle(addedItem);
              tempWidth += addedItem.offsetWidth + parseFloat(style.marginLeft) + parseFloat(style.marginRight);
              if (tempWidth < this.horizontalMenuWidth) {
                numberOfHorizontalTopItems++;
                if (numberOfHorizontalTopItems < this.topItems.length) {
                  this.showMoreHorizontalTopItemsButton = true;
                  if (this.horizontalMenuWidth - tempWidth < 35) {
                    numberOfHorizontalTopItems--;
                  }
                } else {
                  this.showMoreHorizontalTopItemsButton = false;
                  this.navigatorHeight = 40;
                }
              } else {
                break;
              }
            }
            this.horizontalTopItemsToDisplay = this.topItems.slice(0, numberOfHorizontalTopItems);
          }
        }
      }

      // Vertical bar
      this.verticalMenuHeight = this.navigatorHeight - this.smartmenuHeight - 51;
      if (this.navigator.design.menuBar.layout == 'vertical' && !this.droppableTopItemSpotsAreShowing) {
        this.verticalMenuHeight += 22;
      }

      // Content area
      this.contentTop = this.navigator.design.menuBar.layout == 'vertical' ? this.navigatorTop : this.navigatorTop + 40;
      if (this.navigator.design.menuBar.layout == 'horizontal') {
        if (this.isInEditMode) {
          setTimeout(() => {
            this.contentTop = this.navigatorHeight + 48;
          }, 0);
        } else {
          setTimeout(() => {
            this.contentTop = this.navigatorHeight;
            if (this.navigatorContainer != undefined) {
              //this.navigatorContainer.style.height = this.contentHeight - this.navigatorHeight + "px";
            }
          }, 0);
        }
      }
      this.contentHeight = window.innerHeight - this.contentTop - (Helper.getDisplayEnvironment() === "Sharepoint" || Helper.getDisplayEnvironment() === "SharepointClassic" ? -50 : 0);
      if (this.isMobile && Helper.isSharepointInAppBrowser()) {
        this.contentHeight += 50;
      }
      if (this.isInEditMode) {
        if (displayEnvironment === "Sharepoint") {
          this.contentHeight -= 130;
        } else {
          this.contentHeight -= 20;
        }
      }

      if (displayEnvironment === 'Sharepoint') {
        this.contentHeight -= 100;
      }
    }
  }

  @action private hideAllPanels(): void {
    if (
      this.isShowingTopItemSettings ||
      this.isShowingElementGroupSettings ||
      this.isShowingElementSettings ||
      this.isShowingDesignSettings ||
      this.isShowingAnalytics
    ) {
      this.isShowingTopItemSettings = false;
      this.isShowingElementGroupSettings = false;
      this.isShowingElementSettings = false;
      this.isShowingDesignSettings = false;
      this.isShowingAnalytics = false;
    }
  }

  @action public cancelEdit(): void {
    this.navigator = JSON.parse(localStorage.getItem(`navigator-${this.instanceId}`));
    this.hideAllPanels();
    this.topItemIndexToEdit = undefined;
    this.elementGroupColumnIndexToEdit = undefined;
    this.elementGroupIndexToEdit = undefined;
    this.adminPivot = 'Content';
    this.getTopItems();
    this.presentNavigator();
    this.hasChanged = undefined;
    NavigatorService.isCheckedOut(
      `${Environment.getEnvironmentForMainAPI(this.environment)}/api/${this.tenant}/navigator/${this.navigator.version.id
      }/status?instanceName=${this.instance}`,
      this.token,
      this.instance,
    ).then((checkOut: ICheckOut) => {
      if (
        checkOut.author != undefined &&
        this.user != undefined &&
        checkOut.author.email == this.user.userPrincipalName &&
        this.hasChanged == undefined
      ) {
        NavigatorService.checkOut(
          `${Environment.getEnvironmentForMainAPI(this.environment)}/api/${this.tenant}/navigator/${this.navigator.version.id
          }/status?instanceName=${this.instance}&checkedOut=false`,
          this.token,
          this.instance,
        ).then((status: any) => {
          if (status == 200) {
            console.log('You have checked in the navigator!');
          } else {
            console.log('Error checking in the navigator: ' + status);
          }
        });
      }
    });
  }

  @action public checkForChanges(): void {
    if (this.adminPivot === "Analytics") {
      this.getNavigatorClicks(this.fromDate, this.toDate);
    }
    console.log("this.navigator.version.public: " + this.navigator.version.public);
    if (this.navigator && this.navigator.version && !this.navigator.version.public && localStorage.getItem(`navigator-${this.instanceId}`) && localStorage.getItem(`navigator-${this.instanceId}`) != JSON.stringify(this.navigator)) {
      Helper.setLocalStorage(`navigator-modified-${this.instanceId}`, JSON.stringify(this.navigator));
    }
    if (localStorage.getItem(`navigator-${this.instanceId}`) && localStorage.getItem(`navigator-modified-${this.instanceId}`) && localStorage.getItem(`navigator-modified-${this.instanceId}`) != localStorage.getItem(`navigator-${this.instanceId}`)) {
      if (this.hasChanged == undefined) {
        NavigatorService.isCheckedOut(
          `${Environment.getEnvironmentForMainAPI(this.environment)}/api/${this.tenant}/navigator/${this.navigator.version.id
          }/status?instanceName=${this.instance}`,
          this.token,
          this.instance,
        ).then((checkOut: ICheckOut) => {
          if (!this.navigator.version.public) {
            if (
              checkOut == undefined ||
              !checkOut.checkedOut ||
              (checkOut.author && this.user && checkOut.author.email == this.user.userPrincipalName)
            ) {
              NavigatorService.checkOut(
                `${Environment.getEnvironmentForMainAPI(this.environment)}/api/${this.tenant}/navigator/${this.navigator.version.id
                }/status?instanceName=${this.instance}&checkedOut=true`,
                this.token,
                this.instance,
              ).then((status: any) => {
                if (status == 200) {
                  console.log('You have checked out the navigator!');
                } else {
                  console.log('Error checking out the navigator: ' + status);
                }
              });
            } else {
              this.checkOut = checkOut;
              this.checkedOut = true;
              console.log(`${checkOut.author.name} has checked out the navigator!`);
            }
          }
        });
      }
      this.hasChanged = Helper.getRandomStringKey();
    } else {
      this.hasChanged = undefined;
      this.isSaving = false;
      this.isPublishing = false;
    }
  }

  @observable public user: IUser;
  @observable public userRole: IUserRole;

  @action public setUserRole(userRole: IUserRole): void {
    this.userRole = userRole;
    this.user = Helper.syncUserProperties(userRole.user);
  }

  // Version

  @observable public showDeleteDraftPopup: boolean;
  @observable public isDeletingDraft: boolean;

  @action public setVersions(versions: IDraft[]): void {
    this.versions = versions;
  }

  @action public toggleNewVersionPopup(isPublic?: boolean): void {
    this.checkForNewContent();
    if (this.isShowingNewVersionPopup) {
      this.isShowingNewVersionPopup = false;
    } else {
      this.isShowingNewVersionPopup = true;
      if (isPublic) {
        this.newVersionShouldBePublic = true;
      }
    }
  }

  @action public setVersionTitle(title: string): void {
    this.navigator.version.title = title;
  }

  @action public toggleAnalytics(): void {
    this.checkForNewContent();
    this.getContainer();
    if (this.isInEditMode && this.checkedOut) {
      this.selectTopMenuItem(undefined);
    }
    if (this.isShowingAnalytics) {
      this.navigatorContainer.style.zIndex = 1;
      this.adminPivot = 'Content';
      this.hideAllPanels();
      this.isShowingAnalytics = false;
      this.getTopItems();
    }
    else {
      this.navigatorContainer.style.zIndex = '';
      this.hideAllPanels();
      this.isShowingAnalytics = true;
    }
    this.resize();
  }

  @action public getNavigatorClicks(fromDate: Date, toDate: Date): void {
    this.visualizationFailed = false;
    this.isFetchingClicks = true;
    this.doneSettingClicks = false;
    AnalyticsService.getNavigatorClicks(
      this.environment,
      this.token,
      this.tenant,
      this.instance,
      this.dateTimeService.ConvertToDDMMYYYYWithSlashes(fromDate),
      this.dateTimeService.ConvertToDDMMYYYYWithSlashes(toDate))
      .then((navigatorClicks) => {
        this.isFetchingClicks = false;
        this.navigatorClicks = navigatorClicks;
        this.setNavigatorClicks();
      }).catch((e) => {
        this.visualizationFailed = true;
        console.log(e);
      })
  }

  @action public setNavigatorClicks(): void {
    this.leastClickedAmount = Number.POSITIVE_INFINITY;
    this.mostClickedAmount = Number.NEGATIVE_INFINITY;
    this.leastUniqueClickedAmount = Number.POSITIVE_INFINITY;
    this.mostUniqueClickedAmount = Number.NEGATIVE_INFINITY;
    this.totalClicks = 0;
    this.totalUniqueClicks = 0;

    for (let topItemIndex = 0; topItemIndex < this.navigator.megamenu.topItems.length; topItemIndex++) {
      let topItemKey = this.getTitleOrUrl(this.navigator.megamenu.topItems[topItemIndex]?.config.title, this.navigator.megamenu.topItems[topItemIndex]?.config.url)?.trim();
      if (topItemKey) {
        this.navigator.megamenu.topItems[topItemIndex].config.clicks =
          this.setTotalMostLeastClickedReturnClick(this.navigatorClicks[`${topItemKey}-TopItem-${this.instance}`]?.clicks ?? 0, "Total");

        this.navigator.megamenu.topItems[topItemIndex].config.uniqueClicks =
          this.setTotalMostLeastClickedReturnClick(this.navigatorClicks[`${topItemKey}-TopItem-${this.instance}`]?.uniqueClicks ?? 0, "Unique");
      }

      for (let elementGroupColumnIndex = 0; elementGroupColumnIndex < this.navigator.megamenu.topItems[topItemIndex].elementGroups.length; elementGroupColumnIndex++) {
        for (let elementGroupIndex = 0; elementGroupIndex < this.navigator.megamenu.topItems[topItemIndex].elementGroups[elementGroupColumnIndex].length; elementGroupIndex++) {
          let elementGroupKey = this.getTitleOrUrl(this.navigator.megamenu.topItems[topItemIndex].elementGroups[elementGroupColumnIndex][elementGroupIndex]?.config.headline,
            this.navigator.megamenu.topItems[topItemIndex].elementGroups[elementGroupColumnIndex][elementGroupIndex]?.config.url)?.trim();

          if (elementGroupKey) {
            this.navigator.megamenu.topItems[topItemIndex].elementGroups[elementGroupColumnIndex][elementGroupIndex].config.clicks =
              this.setTotalMostLeastClickedReturnClick(this.navigatorClicks[`${elementGroupKey}-ElementGroup-${this.instance}`]?.clicks ?? 0, "Total");

            this.navigator.megamenu.topItems[topItemIndex].elementGroups[elementGroupColumnIndex][elementGroupIndex].config.uniqueClicks =
              this.setTotalMostLeastClickedReturnClick(this.navigatorClicks[`${elementGroupKey}-ElementGroup-${this.instance}`]?.uniqueClicks ?? 0, "Unique");
          }

          for (let elementIndex = 0; elementIndex < this.navigator.megamenu.topItems[topItemIndex].elementGroups[elementGroupColumnIndex][elementGroupIndex].elements.length; elementIndex++) {
            let elementKey = this.getTitleOrUrl(this.navigator.megamenu.topItems[topItemIndex].elementGroups[elementGroupColumnIndex][elementGroupIndex].elements[elementIndex]?.config.text,
              this.navigator.megamenu.topItems[topItemIndex].elementGroups[elementGroupColumnIndex][elementGroupIndex].elements[elementIndex]?.config.url)?.trim();
            if (elementKey) {
              this.navigator.megamenu.topItems[topItemIndex].elementGroups[elementGroupColumnIndex][elementGroupIndex].elements[elementIndex].config.clicks =
                this.setTotalMostLeastClickedReturnClick(this.navigatorClicks[`${elementKey}-Element-${this.instance}`]?.clicks ?? 0, "Total");

              this.navigator.megamenu.topItems[topItemIndex].elementGroups[elementGroupColumnIndex][elementGroupIndex].elements[elementIndex].config.uniqueClicks =
                this.setTotalMostLeastClickedReturnClick(this.navigatorClicks[`${elementKey}-Element-${this.instance}`]?.uniqueClicks ?? 0, "Unique");
            }
          }
        }
      }
    }

    for (let smartMenuIndex = 0; smartMenuIndex < this.navigator.smartmenu.elements.length; smartMenuIndex++) {
      let smartMenuKey = this.getTitleOrUrl(this.navigator.smartmenu.elements[smartMenuIndex]?.config.title, this.navigator.smartmenu.elements[smartMenuIndex]?.config.url) ?? "";

      if (this.navigator.smartmenu.elements[smartMenuIndex].config != undefined) {
        this.navigator.smartmenu.elements[smartMenuIndex].config.clicks =
          this.setTotalMostLeastClickedReturnClick(this.navigatorClicks[`${smartMenuKey}-${this.navigator.smartmenu.elements[smartMenuIndex]?.type}-${this.instance}`]?.clicks ?? 0, "Total");

        this.navigator.smartmenu.elements[smartMenuIndex].config.uniqueClicks =
          this.setTotalMostLeastClickedReturnClick(this.navigatorClicks[`${smartMenuKey}-${this.navigator.smartmenu.elements[smartMenuIndex]?.type}-${this.instance}`]?.uniqueClicks ?? 0, "Unique");
      }
    }

    this.navigator.design.menuBar.logoClicks =
      this.setTotalMostLeastClickedReturnClick(this.navigatorClicks[`Logo--${this.instance}`]?.clicks ?? 0, "Total")

    this.navigator.design.menuBar.logoUniqueClicks =
      this.setTotalMostLeastClickedReturnClick(this.navigatorClicks[`Logo--${this.instance}`]?.uniqueClicks ?? 0, "Unique")

    if (this.totalClicks == 0) {
      this.areThereClicksInPeriod = false;
    }
    else {
      this.areThereClicksInPeriod = true;
      this.getTopItems();
    }
    this.doneSettingClicks = true;
  }

  @action private getTitleOrUrl(title: string, url: string): string {
    return !title ? url : title
  }

  @action private setTotalMostLeastClickedReturnClick(click: number, clickType: string): number {
    if (clickType == "Total") {
      this.mostClickedAmount = click > this.mostClickedAmount ? click : this.mostClickedAmount;
      this.leastClickedAmount = click < this.leastClickedAmount ? click : this.leastClickedAmount;
      this.totalClicks += click;
    }
    if (clickType == "Unique") {
      this.mostUniqueClickedAmount = click > this.mostUniqueClickedAmount ? click : this.mostUniqueClickedAmount;
      this.leastUniqueClickedAmount = click < this.leastUniqueClickedAmount ? click : this.leastUniqueClickedAmount;
      this.totalUniqueClicks += click;
    }
    return click;
  }

  @action public getClickTooltipLocalizationString(click: number): string {
    if (click == 1) {
      return click + " " + this.localizationService.strings.Navigator_Analytics_TooltipSingleClickText + " ";
    }
    return (click ?? 0) + " " + this.localizationService.strings.Navigator_Analytics_TooltipPluralClicksText + " ";
  }

  @action public getHeatMapColorHSL(valueToNormalize0To1: number): string {
    if (valueToNormalize0To1 == 0) {
      return;
    }
    let normalizedValue0To1: number;
    if (this.clickFilter == 'Total') {
      normalizedValue0To1 = ((valueToNormalize0To1 - this.leastClickedAmount) / (this.mostClickedAmount - this.leastClickedAmount));
    }
    else {
      normalizedValue0To1 = ((valueToNormalize0To1 - this.leastUniqueClickedAmount) / (this.mostUniqueClickedAmount - this.leastUniqueClickedAmount));
    }
    if (Number.isNaN(normalizedValue0To1)) {
      normalizedValue0To1 = 1;
    }
    let h = (1.0 - normalizedValue0To1) * 60;
    let s = 100;
    let l = 50;
    return `hsla(${h},${s}%,${l}%, 0.9)`;
  }

  @action public getPercentageOfClickComparedToAllClicks(clicks: number): number {
    return (clicks ?? 0) == 0 ? 0 : Math.round(((clicks / (this.clickFilter == 'Total' ? this.totalClicks : this.totalUniqueClicks)) * 100) * 100) / 100
  }

  @action public GetUserGroupTargetingForNavigatorAnalytics(targeting: any, applicationSettings: string, seperator = ", "): string {
    let itemTargeting: string[] = [];
    if (targeting?.targetingGroups && applicationSettings?.toLocaleLowerCase() === "user groups" && targeting?.enabled) {
      this.applicationSettings.application?.default?.values?.forEach((group) => {
        if (group.id && targeting.targetingGroups.includes((group.id).toString())) {
          itemTargeting.push(group.label);
        }
      });
    }
    return itemTargeting.join(seperator);
  }

  // Design settings
  @action public toggleDesignSettings(): void {
    this.checkForNewContent();
    this.getContainer();
    this.navigatorContainer.style.zIndex = 1;
    if (this.isShowingDesignSettings) {
      this.adminPivot = 'Content';
      this.hideAllPanels();
    } else {
      this.hideAllPanels();
      this.isShowingDesignSettings = true;
    }
    this.resize();
  }

  @action public setLogoUrl(logoUrl: string): void {
    this.navigator.design.menuBar.logo = logoUrl;
    this.checkForChanges();
  }

  @action public setLogoLinkUrl(logoLinkUrl: string): void {
    this.navigator.design.menuBar.logoLinkUrl = logoLinkUrl;
    this.checkForChanges();
  }

  @action public setBackgroundColor(color: string): void {
    this.navigator.design.menuBar.backgroundColor = color;
    this.checkForChanges();
  }

  @action public setIconColor(color: string): void {
    this.navigator.design.menuBar.iconColor = color;
    this.checkForChanges();
  }

  @action public setHighlightColor(color: string): void {
    this.navigator.design.menuBar.highlightColor = color;
    this.checkForChanges();
  }

  @action public setLayout(layoutOption: string): void {
    this.navigator.design.menuBar.layout = layoutOption as 'burger' | 'horizontal' | 'vertical';
    if (layoutOption != 'vertical') {
      this.navigator.design.menuBar.showSlimPanel = false;
    }
    if (this.droppableTopItemSpotsAreShowing) {
      this.hideDroppableTopItemSpots();
    }
    this.checkForChanges();
    setTimeout(() => {
      this.getTopItems();
    }, 0);
    setTimeout(() => {
      this.presentNavigator();
      this.getContainer();
      this.resize();
    }, 0);
  }

  @action public setNumberOfVisibleSmartmenuItems(value: number): void {
    if (!this.isShowingAnalytics && !this.checkedOut) {
      this.navigator.design.menuBar.numberOfVisibleSmartmenuItems = value;
      this.checkForChanges();
      this.resize();
    }
  }

  @action public toggleSlimPanel(): void {
    this.navigator.design.menuBar.showSlimPanel =
      this.navigator.design.menuBar.showSlimPanel != undefined ? !this.navigator.design.menuBar.showSlimPanel : true;
    this.checkForChanges();
    this.resize();
  }

  // Top items settings

  @action public addTopItem(): void {
    this.adminPivot = 'Content';
    this.checkForNewContent();
    this.hideAllPanels();
    this.isShowingTopItemSettings = false;
    if (this.droppableTopItemSpotsAreShowing) {
      this.hideDroppableTopItemSpots();
    }
    this.isCreatingContent = true;
    this.topItemIndexToEdit = this.navigator.megamenu.topItems.length;
    this.navigator.megamenu.topItems.push({
      type: 'menu',
      config: { title: '' },
      elementGroups: [],
    });

    setTimeout(() => {
      this.isShowingTopItemSettings = true;
    }, 0);
    this.getTopItems();
    this.checkForChanges();
    this.resize();
  }

  @action public reorderTopItem(): void {
    this.adminPivot = 'Content';
    this.checkForNewContent();
    this.hideAllPanels();
    this.isShowingTopItemSettings = false;
    this.droppableTopItemSpotsAreShowing = true;
    this.droppableElementGroupSpotsAreShowing = false;
    this.droppableElementSpotsAreShowing = false;

    setTimeout(() => {
      this.isShowingTopItemSettings = true;
    }, 0);
    // this.getTopItems();
    this.checkForChanges();
    this.resize();
  }


  @action public closeReorderTopItem(): void {
    this.adminPivot = 'Content';
    this.checkForNewContent();
    this.hideAllPanels();
    this.isShowingTopItemSettings = false;
    this.droppableTopItemSpotsAreShowing = false;
    setTimeout(() => {
      this.isShowingTopItemSettings = false;
    }, 0);
    this.getTopItems();
    this.checkForChanges();
    this.resize();
  }

  @action public reorderSmartMenu(): void {
    this.adminPivot = 'Content';
    this.checkForNewContent();
    // this.topItemIndexToEdit = undefined;
    this.hideAllPanels();
    // this.isShowingTopItemSettings = false;
    this.droppableSmartmentTopItemSpotsAreShowing = true;
    setTimeout(() => {
      this.isShowingTopItemSettings = true;
      this.resize();
    }, 0);
    this.checkForChanges();
  }

  @action public closeReorderSmartMenu(): void {
    this.adminPivot = 'Content';
    this.checkForNewContent();
    // this.topItemIndexToEdit = undefined;
    this.hideAllPanels();
    this.droppableSmartmentTopItemSpotsAreShowing = false;
    // this.isShowingAdditionalSmartmenuItems = !this.isShowingAdditionalSmartmenuItems;
    setTimeout(() => {
      this.isShowingTopItemSettings = false;
      this.resize();
    }, 0);
    this.checkForChanges();
  }

  @action public addTopItemToSmartmenu(): void {
    this.adminPivot = 'Content';
    this.checkForNewContent();
    this.topItemIndexToEdit = undefined;
    this.hideAllPanels();
    this.isShowingTopItemSettings = false;
    this.isCreatingContent = true;
    this.smartmenuTopItemIndexToEdit = this.navigator.smartmenu.elements.length;
    this.navigator.smartmenu.elements.push({
      type: 'menu',
      config: { title: '', icon: 'Link' },
      elementGroups: [],
    });
    setTimeout(() => {
      this.isShowingTopItemSettings = true;
      this.resize();
    }, 0);
    this.checkForChanges();
  }

  @action public checkForNewContent(): void {
    if (this.isCreatingContent) {
      // Top item
      let topItem = this.navigator.megamenu.topItems[this.topItemIndexToEdit];
      if (
        (topItem != undefined && topItem.config.title == undefined && topItem.config.icon == undefined) ||
        (topItem != undefined && topItem.config.title == '' && topItem.config.icon == undefined)
      ) {
        this.deleteSelectedTopItem();
      }
      // Smartmenu top item
      let smartmenuTopItem = this.navigator.megamenu.topItems[this.smartmenuTopItemIndexToEdit];
      if (
        (smartmenuTopItem != undefined &&
          smartmenuTopItem.config.title == undefined &&
          smartmenuTopItem.config.icon == undefined) ||
        (smartmenuTopItem != undefined &&
          smartmenuTopItem.config.title == '' &&
          smartmenuTopItem.config.icon == undefined)
      ) {
        this.deleteSelectedSmartmenuTopItem();
      }
    }
  }

  @action public toggleTopItemSettings(): void {
    this.adminPivot = 'Content';
    this.isCreatingContent = false;
    this.hideAllPanels();
    if (this.topItemIndexToEdit != undefined || this.smartmenuTopItemIndexToEdit != undefined) {
      this.isShowingTopItemSettings = true;
    }
    if (this.topItemIndexToEdit != undefined) {
      this.getTopItems();
    }
    this.checkForChanges();
    setTimeout(() => {
      this.resize();
    }, 0);
  }

  @action public deleteSelectedTopItem(): void {
    this.navigator.megamenu.topItems.splice(this.topItemIndexToEdit, 1);
    this.getTopItems();
    this.topItemIndexToEdit = undefined;
    this.isShowingTopItemSettings = false;
    this.checkForChanges();
    this.hideAllPanels();
    this.resize();
  }

  @action public deleteSelectedSmartmenuTopItem(): void {
    this.navigator.smartmenu.elements.splice(this.smartmenuTopItemIndexToEdit, 1);
    this.smartmenuTopItemIndexToEdit = undefined;
    this.isShowingTopItemSettings = false;
    this.hideAllPanels();
    this.checkForChanges();
    setTimeout(() => {
      this.resize();
    }, 0);
  }

  @action public setTopItemType(type: string): void {
    this.navigator.megamenu.topItems[this.topItemIndexToEdit].type = type;
    if (type == 'phonebook') {
      this.setTopItemIcon('Phone');
    }
    if (type == 'menu') {
      this.setTopItemIcon('Link');
    }
    if (type == 'event') {
      this.setTopItemIcon('Calendar');
      this.navigator.megamenu.topItems[this.topItemIndexToEdit].config.showPeriod = 'week';
      this.navigator.megamenu.topItems[this.topItemIndexToEdit].config.numberOfEvents = 4;
    }
    if (type == 'contentcreator') {
      this.setTopItemIcon('Content Creator');
      this.navigator.megamenu.topItems[this.topItemIndexToEdit].config.title = 'Content Creator';
    }
    this.getTopItems();
    this.checkForChanges();
  }

  @action public setSmartmenuTopItemType(type: string): void {
    if (type !== this.navigator.smartmenu.elements[this.smartmenuTopItemIndexToEdit].type) {
      this.navigator.smartmenu.elements[this.smartmenuTopItemIndexToEdit].type = type;
      if (type == 'phonebook') {
        this.setSmartmenuTopItemIcon('Phone');
      }
      if (type == 'celebration') {
        this.navigator.smartmenu.elements[this.smartmenuTopItemIndexToEdit].config.subType = "confetti";
        this.setSmartmenuTopItemIcon('Confeti');
      }
      if (type == 'menu') {
        this.setSmartmenuTopItemIcon('Link');
      }
      if (type == 'event') {
        this.setSmartmenuTopItemIcon('Calendar');
        this.navigator.smartmenu.elements[this.smartmenuTopItemIndexToEdit].config.showPeriod = 'week';
        this.navigator.smartmenu.elements[this.smartmenuTopItemIndexToEdit].config.numberOfEvents = 4;
      }
      if (type == 'contentcreator') {
        this.setSmartmenuTopItemIcon('Content Creator');
        this.navigator.smartmenu.elements[this.smartmenuTopItemIndexToEdit].config.title = 'Content Creator';
      }
      if (type == 'ai') {
        this.setSmartmenuTopItemIcon('Bot');
        this.navigator.smartmenu.elements[this.smartmenuTopItemIndexToEdit].config.title = 'AI assistent';
        this.navigator.smartmenu.elements[this.smartmenuTopItemIndexToEdit].config.instance = {};
        this.navigator.smartmenu.elements[this.smartmenuTopItemIndexToEdit].config.aiContentLanguage = "en";
      }
    }
    this.checkForChanges();
  }

  @action public setSmartmenuSubType(subType: string): void {
    this.navigator.smartmenu.elements[this.smartmenuTopItemIndexToEdit].config.subType = subType;
    if (subType == 'snow') {
      this.setSmartmenuTopItemIcon('Snowflake');
    }
    if (subType == 'confetti') {
      this.setSmartmenuTopItemIcon('Confeti');
    }
    if (subType == 'easterconfetti') {
      this.setSmartmenuTopItemIcon('Confeti');
    }
    if (subType == 'halloweenconfetti') {
      this.setSmartmenuTopItemIcon('Halloween');
    }
    this.checkForChanges();
  }

  @action public setTopItemTitle(title: string): void {
    this.navigator.megamenu.topItems[this.topItemIndexToEdit].config.title = title;
    this.getTopItems();
    this.checkForChanges();
  }

  @action public setSmartmenuTopItemTitle(title: string): void {
    this.navigator.smartmenu.elements[this.smartmenuTopItemIndexToEdit].config.title = title;
    this.checkForChanges();
  }

  @action public setTopItemUrl(url: string): void {
    this.navigator.megamenu.topItems[this.topItemIndexToEdit].config.url = url;
    this.getTopItems();
    this.checkForChanges();
  }

  @action public setSmartmenuTopItemUrl(url: string): void {
    this.navigator.smartmenu.elements[this.smartmenuTopItemIndexToEdit].config.url = url;
    this.checkForChanges();
  }

  @action public setTopItemOpenInNewWindow(): void {
    this.navigator.megamenu.topItems[this.topItemIndexToEdit].config.openInNewWindow = !this.navigator.megamenu
      .topItems[this.topItemIndexToEdit].config.openInNewWindow;
    this.getTopItems();
    this.checkForChanges();
  }

  @action public setSmartmenuTopItemOpenInNewWindow(): void {
    this.navigator.smartmenu.elements[this.smartmenuTopItemIndexToEdit].config.openInNewWindow = !this.navigator
      .smartmenu.elements[this.smartmenuTopItemIndexToEdit].config.openInNewWindow;
    this.checkForChanges();
  }

  @action public setSmartmenuTopItemEnableLikes(): void {
    this.navigator.smartmenu.elements[this.smartmenuTopItemIndexToEdit].config.enableLikes =
      this.navigator.smartmenu.elements[this.smartmenuTopItemIndexToEdit].config.enableLikes != undefined
        ? !this.navigator.smartmenu.elements[this.smartmenuTopItemIndexToEdit].config.enableLikes
        : false;
    this.checkForChanges();
  }

  @action public setTopItemEnableLikes(): void {
    this.navigator.megamenu.topItems[this.topItemIndexToEdit].config.enableLikes =
      this.navigator.megamenu.topItems[this.topItemIndexToEdit].config.enableLikes != undefined
        ? !this.navigator.megamenu.topItems[this.topItemIndexToEdit].config.enableLikes
        : false;
    this.checkForChanges();
  }

  @action public setSmartmenuTopItemEnableComments(): void {
    this.navigator.smartmenu.elements[this.smartmenuTopItemIndexToEdit].config.enableComments =
      this.navigator.smartmenu.elements[this.smartmenuTopItemIndexToEdit].config.enableComments != undefined
        ? !this.navigator.smartmenu.elements[this.smartmenuTopItemIndexToEdit].config.enableComments
        : false;
    this.checkForChanges();
  }

  @action public setTopItemEnableComments(): void {
    this.navigator.megamenu.topItems[this.topItemIndexToEdit].config.enableComments =
      this.navigator.megamenu.topItems[this.topItemIndexToEdit].config.enableComments != undefined
        ? !this.navigator.megamenu.topItems[this.topItemIndexToEdit].config.enableComments
        : false;
    this.checkForChanges();
  }

  @action public setTopItemSiteCollection(siteCollection: string): void {
    this.navigator.megamenu.topItems[this.topItemIndexToEdit].config.siteCollection = siteCollection;
    this.getTopItems();
    this.checkForChanges();
  }

  @action public setTopItemResultSource(resultSource: string): void {
    this.navigator.megamenu.topItems[this.topItemIndexToEdit].config.resultSource = resultSource;
    this.getTopItems();
    this.checkForChanges();
  }

  @action public updateTargeting(targeting: any): void {
    this.navigator.megamenu.topItems[this.topItemIndexToEdit].targeting = targeting;
    this.getTopItems();
    this.checkForChanges();
  }

  @action public updateSmartmenuTopIteTargeting(targeting: any): void {
    this.navigator.smartmenu.elements[this.smartmenuTopItemIndexToEdit].targeting = targeting;
    this.getTopItems();
    this.checkForChanges();
  }

  @action public setSmartmenuTopItemSiteCollection(siteCollection: string): void {
    this.navigator.smartmenu.elements[this.smartmenuTopItemIndexToEdit].config.siteCollection = siteCollection;
    this.checkForChanges();
  }

  @action public setSmartmenuTopItemResultSource(resultSource: string): void {
    this.navigator.smartmenu.elements[this.smartmenuTopItemIndexToEdit].config.resultSource = resultSource;
    this.checkForChanges();
  }

  @action public setTopItemIcon(icon: string): void {
    this.navigator.megamenu.topItems[this.topItemIndexToEdit].config.icon = icon;
    this.getTopItems();
    this.checkForChanges();
  }

  @action public setSmartmenuTopItemIcon(icon: string): void {
    this.navigator.smartmenu.elements[this.smartmenuTopItemIndexToEdit].config.icon = icon;
    this.getTopItems();
    this.checkForChanges();
  }

  @action public setTopItemCode(code: string): void {
    this.navigator.megamenu.topItems[this.topItemIndexToEdit].config.code = code;
    this.getTopItems();
    this.checkForChanges();
  }

  @action public setSmartmenuTopItemCode(code: string): void {
    this.navigator.smartmenu.elements[this.smartmenuTopItemIndexToEdit].config.code = code;
    this.getTopItems();
    this.checkForChanges();
  }

  @action public setTopItemFont(font: string): void {
    this.navigator.design.topItems.font = font;
    this.getTopItems();
    this.checkForChanges();
  }

  @action public setTopItemTextColor(color: string): void {
    this.navigator.design.topItems.textColor = color;
    this.getTopItems();
    this.checkForChanges();
  }

  @action public setBurgerMenuBackgroundColor(color: string): void {
    this.navigator.design.topItems.backgroundColor = color;
    this.checkForChanges();
  }

  @action public setBurgerMenuHighlightColor(color: string): void {
    this.navigator.design.topItems.highlightColor = color;
    this.checkForChanges();
  }

  @action public setBurgerMenuHighlightTextColor(color: string): void {
    this.navigator.design.topItems.highlightTextColor = color;
    this.checkForChanges();
  }

  @action public setTopItemFontSize(fontSize: Property.FontSize<number>): void {
    this.navigator.design.topItems.fontSize = fontSize;
    this.getTopItems();
    this.checkForChanges();
  }

  @action public setTopItemFontWeight(fontWeight): void {
    this.navigator.design.topItems.fontWeight = fontWeight;
    this.getTopItems();
    this.checkForChanges();
  }

  @action public setTopItemTextTransform(textTransform: Property.TextTransform): void {
    this.navigator.design.topItems.textTransform = textTransform;
    this.getTopItems();
    this.checkForChanges();
  }

  @action public setTopItemLetterSpacing(letterSpacing: string): void {
    this.navigator.design.topItems.letterSpacing = letterSpacing;
    this.getTopItems();
    this.checkForChanges();
  }

  @action public setTopItemMargin(margin: string): void {
    this.navigator.design.topItems.margin = margin;
    this.getTopItems();
    this.checkForChanges();
  }

  @action public toggleTopItemLine(): void {
    this.navigator.design.topItems.showLine = !this.navigator.design.topItems.showLine;
    this.getTopItems();
    this.checkForChanges();
  }

  @action public setTopItemLineThickness(lineThickness: string): void {
    this.navigator.design.topItems.lineThickness = lineThickness;
    this.getTopItems();
    this.checkForChanges();
  }

  @action public setTopItemIconColor(color: string): void {
    this.navigator.design.topItems.iconColor = color;
    this.checkForChanges();
  }

  @action public showDroppableTopItemSpots(): void {
    this.droppableTopItemSpotsAreShowing = true;
    this.droppableElementGroupSpotsAreShowing = false;
    this.droppableElementSpotsAreShowing = false;
  }

  @action public hideDroppableTopItemSpots(): void {
    this.droppableTopItemSpotsAreShowing = false;
  }

  @action public setDraggedTopItem(draggedTopItemIndex: number): void {
    this.draggedTopItemIndex = draggedTopItemIndex;
  }

  @action public updateTopItemPosition(newIndex: number): void {
    if (this.droppableTopItemSpotsAreShowing) {
      this.navigator.megamenu.topItems.splice(newIndex, 0, this.navigator.megamenu.topItems[this.draggedTopItemIndex]);
      if (newIndex < this.draggedTopItemIndex) {
        this.navigator.megamenu.topItems.splice(this.draggedTopItemIndex + 1, 1);
      } else {
        this.navigator.megamenu.topItems.splice(this.draggedTopItemIndex, 1);
      }
      this.getTopItems();
    } else if (this.droppableSmartmentTopItemSpotsAreShowing) {
      this.navigator.smartmenu.elements.splice(
        newIndex,
        0,
        this.navigator.smartmenu.elements[this.draggedTopItemIndex],
      );
      if (newIndex < this.draggedTopItemIndex) {
        this.navigator.smartmenu.elements.splice(this.draggedTopItemIndex + 1, 1);
      } else {
        this.navigator.smartmenu.elements.splice(this.draggedTopItemIndex, 1);
      }
    }
    this.draggedTopItemIndex = undefined;
    this.checkForChanges();
  }

  // Element Groups settings

  @action public addElementGroup(topItemIndexToEdit?: number, elementGroupColumnIndexToEdit?: number): void {
    this.hideAllPanels();
    this.checkForNewContent();
    this.topItemIndexToEdit = topItemIndexToEdit;
    if (
      elementGroupColumnIndexToEdit == undefined &&
      this.navigator.megamenu.topItems[this.topItemIndexToEdit] != undefined
    ) {
      this.elementGroupColumnIndexToEdit = this.navigator.megamenu.topItems[
        this.topItemIndexToEdit
      ].elementGroups.length;
      this.navigator.megamenu.topItems[this.topItemIndexToEdit].elementGroups.push([]);
      this.isCreatingContent = true;
      this.elementGroupIndexToEdit = 0;
      this.navigator.megamenu.topItems[this.topItemIndexToEdit].elementGroups[this.elementGroupColumnIndexToEdit].push({
        config: { clicks: 0, uniqueClicks: 0 },
        elements: [],
        type: "link"
      });
    } else {
      this.elementGroupColumnIndexToEdit = elementGroupColumnIndexToEdit;
      this.isCreatingContent = true;
      if (
        this.navigator.megamenu.topItems[this.topItemIndexToEdit].elementGroups[this.elementGroupColumnIndexToEdit] !=
        undefined
      ) {
        this.elementGroupIndexToEdit = this.navigator.megamenu.topItems[this.topItemIndexToEdit].elementGroups[
          this.elementGroupColumnIndexToEdit
        ].length;
        this.navigator.megamenu.topItems[this.topItemIndexToEdit].elementGroups[
          this.elementGroupColumnIndexToEdit
        ].push({
          config: {},
          elements: [],
          type: "link"
        });
      }
    }
    setTimeout(() => {
      this.isShowingElementGroupSettings = true;
      this.resize();
    }, 0);
  }

  @action public toggleElementGroupSettings(
    topItemIndexToEdit?: number,
    elementGroupColumnIndexToEdit?: number,
    elementGroupIndex?: number,
  ): void {
    this.adminPivot = 'Content';
    this.checkForNewContent();
    this.topItemIndexToEdit = topItemIndexToEdit;
    this.elementGroupColumnIndexToEdit = elementGroupColumnIndexToEdit;
    this.isCreatingContent = false;
    if (elementGroupIndex == undefined) {
      this.hideAllPanels();
    } else {
      this.elementGroupIndexToEdit = elementGroupIndex;
      this.hideAllPanels();
      this.isShowingElementGroupSettings = true;
    }
    this.checkForChanges();
    this.resize();
  }

  @action public deleteSelectedElementGroup(): void {
    this.navigator.megamenu.topItems[this.topItemIndexToEdit].elementGroups[this.elementGroupColumnIndexToEdit].splice(
      this.elementGroupIndexToEdit,
      1,
    );
    if (
      this.navigator.megamenu.topItems[this.topItemIndexToEdit].elementGroups[this.elementGroupColumnIndexToEdit]
        .length == 0
    ) {
      this.navigator.megamenu.topItems[this.topItemIndexToEdit].elementGroups.splice(
        this.elementGroupColumnIndexToEdit,
        1,
      );
      this.elementGroupColumnIndexToEdit = undefined;
    }
    this.hideAllPanels();
    this.checkForChanges();
    this.resize();
  }

  @action public setElementGroupHeadline(headline: string): void {
    this.navigator.megamenu.topItems[this.topItemIndexToEdit].elementGroups[this.elementGroupColumnIndexToEdit][
      this.elementGroupIndexToEdit
    ].config.headline = headline;
    this.checkForChanges();
  }

  @action public setElementGroupImageUrl(imageUrl: string): void {
    this.navigator.megamenu.topItems[this.topItemIndexToEdit].elementGroups[this.elementGroupColumnIndexToEdit][
      this.elementGroupIndexToEdit
    ].config.imageUrl = imageUrl;
    this.checkForChanges();
  }

  @action public setElementGroupType(type: string): void {
    this.navigator.megamenu.topItems[this.topItemIndexToEdit].elementGroups[this.elementGroupColumnIndexToEdit][
      this.elementGroupIndexToEdit
    ].type = type;
    this.checkForChanges();
  }

  @action public setElementGroupUrl(url: string): void {
    this.navigator.megamenu.topItems[this.topItemIndexToEdit].elementGroups[this.elementGroupColumnIndexToEdit][
      this.elementGroupIndexToEdit
    ].config.url = url;
    this.checkForChanges();
  }

  @action public setElementGroupIcon(icon: string): void {
    this.navigator.megamenu.topItems[this.topItemIndexToEdit].elementGroups[this.elementGroupColumnIndexToEdit][
      this.elementGroupIndexToEdit
    ].config.icon = icon;
    this.checkForChanges();
  }

  @action public setElementGroupOpenInNewWindow(): void {
    this.navigator.megamenu.topItems[this.topItemIndexToEdit].elementGroups[this.elementGroupColumnIndexToEdit][
      this.elementGroupIndexToEdit
    ].config.openInNewWindow = !this.navigator.megamenu.topItems[this.topItemIndexToEdit].elementGroups[
      this.elementGroupColumnIndexToEdit
    ][this.elementGroupIndexToEdit].config.openInNewWindow;
    this.checkForChanges();
  }

  @action public setElementGroupFont(font: string): void {
    this.navigator.design.elementGroups.font = font;
    this.checkForChanges();
  }

  @action public setElementGroupTextColor(color: string): void {
    this.navigator.design.elementGroups.textColor = color;
    this.checkForChanges();
  }

  @action public setElementGroupBackgroundColor(color: string): void {
    this.navigator.design.elementGroups.backgroundColor = color;
    this.checkForChanges();
  }

  @action public setElementGroupFontSize(fontSize: Property.FontSize<number>): void {
    this.navigator.design.elementGroups.fontSize = fontSize;
    this.checkForChanges();
  }

  @action public setElementGroupFontWeight(fontWeight): void {
    this.navigator.design.elementGroups.fontWeight = fontWeight;
    this.checkForChanges();
  }

  @action public setElementGroupTextTransform(textTransform: Property.TextTransform): void {
    this.navigator.design.elementGroups.textTransform = textTransform;
    this.checkForChanges();
  }

  @action public setElementGroupLetterSpacing(letterSpacing: string): void {
    this.navigator.design.elementGroups.letterSpacing = letterSpacing;
    this.checkForChanges();
  }

  @action public setElementGroupPadding(padding: string): void {
    this.navigator.design.elementGroups.padding = padding;
    this.checkForChanges();
  }

  @action public toggleElementGroupLine(): void {
    this.navigator.design.elementGroups.showLine = !this.navigator.design.elementGroups.showLine;
    this.checkForChanges();
  }

  @action public setElementGroupLineThickness(lineThickness: string): void {
    this.navigator.design.elementGroups.lineThickness = lineThickness;
    this.checkForChanges();
  }

  @action public setElementGroupIconColor(color: string): void {
    this.navigator.design.elementGroups.iconColor = color;
    this.checkForChanges();
  }

  @action public showDroppableElementGroupSpots(): void {
    this.droppableElementGroupSpotsAreShowing = true;
    this.droppableElementSpotsAreShowing = false;
    this.droppableTopItemSpotsAreShowing = false;
  }

  @action public hideDroppableElementGroupSpots(): void {
    this.droppableElementGroupSpotsAreShowing = false;
  }

  @action public setDraggedElementGroup(
    draggedTopItemIndex: number,
    draggedColumnIndex: number,
    draggedElementGroupIndex: number,
  ): void {
    this.draggedTopItemIndex = draggedTopItemIndex;
    this.draggedColumnIndex = draggedColumnIndex;
    this.draggedElementGroupIndex = draggedElementGroupIndex;
  }

  @action public updateElementGroupPosition(
    topItemIndex: number,
    columnIndex: number,
    elememntGroupIndex: number,
  ): void {
    this.navigator.megamenu.topItems[topItemIndex].elementGroups[columnIndex].splice(
      elememntGroupIndex,
      0,
      this.navigator.megamenu.topItems[this.draggedTopItemIndex].elementGroups[this.draggedColumnIndex][
      this.draggedElementGroupIndex
      ],
    );
    if (
      elememntGroupIndex < this.draggedElementGroupIndex &&
      columnIndex == this.draggedColumnIndex &&
      topItemIndex == this.draggedTopItemIndex
    ) {
      this.navigator.megamenu.topItems[this.draggedTopItemIndex].elementGroups[this.draggedColumnIndex].splice(
        this.draggedElementGroupIndex + 1,
        1,
      );
    } else {
      this.navigator.megamenu.topItems[this.draggedTopItemIndex].elementGroups[this.draggedColumnIndex].splice(
        this.draggedElementGroupIndex,
        1,
      );
    }
    this.draggedTopItemIndex = undefined;
    this.draggedColumnIndex = undefined;
    this.draggedElementGroupIndex = undefined;
    this.checkForChanges();
  }

  // Element settings

  @action public deleteSelectedElement(): void {
    this.navigator.megamenu.topItems[this.topItemIndexToEdit].elementGroups[this.elementGroupColumnIndexToEdit][
      this.elementGroupIndexToEdit
    ].elements.splice(this.elementIndexToEdit, 1);
    this.isCreatingContent = false;
    this.elementIndexToEdit = undefined;
    this.toggleElementSettings();
  }

  @action public toggleElementSettings(
    topItemIndexToEdit?: number,
    elementGroupColumnIndexToEdit?: number,
    elementGroupIndex?: number,
    elementIndex?: number,
  ): void {
    this.adminPivot = 'Content';
    this.checkForNewContent();
    this.isCreatingContent = false;
    if (this.isShowingElementSettings && elementIndex == undefined) {
      this.hideAllPanels();
      let element = this.navigator.megamenu.topItems[this.topItemIndexToEdit].elementGroups[
        this.elementGroupColumnIndexToEdit
      ][this.elementGroupIndexToEdit].elements[this.elementIndexToEdit];
      if (
        (element != undefined && element.type == 'link' && element.config.text == undefined) ||
        (element != undefined && element.type == 'link' && element.config.text == '')
      ) {
        this.navigator.megamenu.topItems[this.topItemIndexToEdit].elementGroups[this.elementGroupColumnIndexToEdit][
          this.elementGroupIndexToEdit
        ].elements.splice(-1, 1);
      }
    } else {
      this.hideAllPanels();
      this.topItemIndexToEdit = topItemIndexToEdit;
      this.elementGroupColumnIndexToEdit = elementGroupColumnIndexToEdit;
      this.elementGroupIndexToEdit = elementGroupIndex;
      this.elementIndexToEdit = elementIndex;
      this.isShowingElementSettings = true;
    }
    this.checkForChanges();
    this.resize();
  }

  @action public addElement(
    topItemIndexToEdit?: number,
    elementGroupColumnIndexToEdit?: number,
    elementGroupIndex?: number,
  ): void {
    this.hideAllPanels();
    this.checkForNewContent();
    this.isShowingElementSettings = false;
    this.isCreatingContent = true;
    this.topItemIndexToEdit = topItemIndexToEdit;
    this.elementGroupColumnIndexToEdit = elementGroupColumnIndexToEdit;
    this.elementGroupIndexToEdit = elementGroupIndex;
    this.elementIndexToEdit = this.navigator.megamenu.topItems[this.topItemIndexToEdit].elementGroups[
      this.elementGroupColumnIndexToEdit
    ][this.elementGroupIndexToEdit].elements.length;
    this.navigator.megamenu.topItems[this.topItemIndexToEdit].elementGroups[this.elementGroupColumnIndexToEdit][
      this.elementGroupIndexToEdit
    ].elements.push({
      type: 'link',
      config: { clicks: 0, uniqueClicks: 0 },
    });
    setTimeout(() => {
      this.isShowingElementSettings = true;
      this.checkForChanges();
      this.resize();
    }, 0);
  }

  @action public setElementType(type: string): void {
    this.navigator.megamenu.topItems[this.topItemIndexToEdit].elementGroups[this.elementGroupColumnIndexToEdit][
      this.elementGroupIndexToEdit
    ].elements[this.elementIndexToEdit].type = type;
    this.checkForChanges();
  }

  @action public setElementTitle(text: string): void {
    this.navigator.megamenu.topItems[this.topItemIndexToEdit].elementGroups[this.elementGroupColumnIndexToEdit][
      this.elementGroupIndexToEdit
    ].elements[this.elementIndexToEdit].config.text = text;
    this.checkForChanges();
  }

  @action public setElementUrl(url: string): void {
    this.navigator.megamenu.topItems[this.topItemIndexToEdit].elementGroups[this.elementGroupColumnIndexToEdit][
      this.elementGroupIndexToEdit
    ].elements[this.elementIndexToEdit].config.url = url;
    this.checkForChanges();
  }

  @action public setElementImageUrl(imageUrl: string): void {
    this.navigator.megamenu.topItems[this.topItemIndexToEdit].elementGroups[this.elementGroupColumnIndexToEdit][
      this.elementGroupIndexToEdit
    ].elements[this.elementIndexToEdit].config.imageUrl = imageUrl;
    this.checkForChanges();
  }

  @action public setElementOpenInNewWindow(): void {
    if (
      this.navigator.megamenu.topItems[this.topItemIndexToEdit].elementGroups[this.elementGroupColumnIndexToEdit][
        this.elementGroupIndexToEdit
      ].elements[this.elementIndexToEdit].config.openInNewWindow == undefined ||
      !this.navigator.megamenu.topItems[this.topItemIndexToEdit].elementGroups[this.elementGroupColumnIndexToEdit][
        this.elementGroupIndexToEdit
      ].elements[this.elementIndexToEdit].config.openInNewWindow
    ) {
      this.navigator.megamenu.topItems[this.topItemIndexToEdit].elementGroups[this.elementGroupColumnIndexToEdit][
        this.elementGroupIndexToEdit
      ].elements[this.elementIndexToEdit].config.openInNewWindow = true;
    } else {
      this.navigator.megamenu.topItems[this.topItemIndexToEdit].elementGroups[this.elementGroupColumnIndexToEdit][
        this.elementGroupIndexToEdit
      ].elements[this.elementIndexToEdit].config.openInNewWindow = false;
    }
    this.checkForChanges();
  }

  @action public setElementIcon(icon: string) {
    this.navigator.megamenu.topItems[this.topItemIndexToEdit].elementGroups[this.elementGroupColumnIndexToEdit][
      this.elementGroupIndexToEdit
    ].elements[this.elementIndexToEdit].config.icon = icon;
    this.checkForChanges();
  }

  @action public setElementFont(font: string): void {
    this.navigator.design.elements.font = font;
    this.checkForChanges();
  }

  @action public setElementTextColor(color: string): void {
    this.navigator.design.elements.textColor = color;
    this.checkForChanges();
  }

  @action public setElementIconColor(color: string): void {
    this.navigator.design.elements.iconColor = color;
    this.checkForChanges();
  }

  @action public setElementFontSize(fontSize: Property.FontSize<number>): void {
    this.navigator.design.elements.fontSize = fontSize;
    this.checkForChanges();
  }

  @action public setElementFontWeight(fontWeight): void {
    this.navigator.design.elements.fontWeight = fontWeight;
    this.checkForChanges();
  }

  @action public setElementTextTransform(textTransform: Property.TextTransform): void {
    this.navigator.design.elements.textTransform = textTransform;
    this.checkForChanges();
  }

  @action public setElementLetterSpacing(letterSpacing: string): void {
    this.navigator.design.elements.letterSpacing = letterSpacing;
    this.checkForChanges();
  }

  @action public setElementPadding(padding: string): void {
    this.navigator.design.elements.padding = padding;
    this.checkForChanges();
  }

  @action public setElementIndentation(indentation: string): void {
    this.navigator.design.elements.indentation = indentation;
    this.checkForChanges();
  }

  @action public showDroppableElementSpots(): void {
    this.droppableElementSpotsAreShowing = true;
    this.droppableElementGroupSpotsAreShowing = false;
    this.droppableTopItemSpotsAreShowing = false;
  }

  @action public hideDroppableElementSpots(): void {
    this.droppableElementSpotsAreShowing = false;
  }

  @action public setDraggedElement(
    draggedTopItemIndex: number,
    draggedColumnIndex: number,
    draggedElementGroupIndex: number,
    draggedElementIndex: number,
  ): void {
    this.draggedTopItemIndex = draggedTopItemIndex;
    this.draggedColumnIndex = draggedColumnIndex;
    this.draggedElementGroupIndex = draggedElementGroupIndex;
    this.draggedElementIndex = draggedElementIndex;
  }

  @action public updateElementPosition(
    topItemIndex: number,
    columnIndex: number,
    elememntGroupIndex: number,
    elementIndex: number,
  ): void {
    this.navigator.megamenu.topItems[topItemIndex].elementGroups[columnIndex][elememntGroupIndex].elements.splice(
      elementIndex,
      0,
      this.navigator.megamenu.topItems[this.draggedTopItemIndex].elementGroups[this.draggedColumnIndex][
        this.draggedElementGroupIndex
      ].elements[this.draggedElementIndex],
    );
    if (
      elementIndex < this.draggedElementIndex &&
      columnIndex == this.draggedColumnIndex &&
      elememntGroupIndex == this.draggedElementGroupIndex &&
      topItemIndex == this.draggedTopItemIndex
    ) {
      this.navigator.megamenu.topItems[this.draggedTopItemIndex].elementGroups[this.draggedColumnIndex][
        this.draggedElementGroupIndex
      ].elements.splice(this.draggedElementIndex + 1, 1);
    } else {
      this.navigator.megamenu.topItems[this.draggedTopItemIndex].elementGroups[this.draggedColumnIndex][
        this.draggedElementGroupIndex
      ].elements.splice(this.draggedElementIndex, 1);
    }
    this.draggedElementIndex = undefined;
    this.checkForChanges();
  }

  // Megamenu

  @action public toggleMegamenu(): void {
    if (!this.isShowingMegamenu && this.navigator != undefined && this.navigator.design.menuBar.layout === 'burger') {
      if (this.droppableTopItemSpotsAreShowing) {
        this.hideDroppableTopItemSpots();
      }
      if (!this.isMobile) {
        this.selectTopMenuItem(0);
      }
    }
    this.isShowingMegamenu = !this.isShowingMegamenu;
    if (this.isShowingMegamenu) {
      document.body.style.overflowY = 'hidden';
      document.body.style.height = '100vh';
    } else {
      document.body.style.overflowY = 'auto';
      document.body.style.height = 'inherit';
    }
    this.isShowingAdditionalSmartmenuItems = false;
    this.checkForChanges();
  }

  @action public selectTopMenuItem(index: number): void {
    this.selectedTopMenuItemIndex = index;
    this.isShowingAdditionalSmartmenuItems = false;
    setTimeout(() => {
      this.resize();
    }, 0);
  }

  @action public getTopItems(): void {
    const topItems: JSX.Element[] = [];
    this.navigator.megamenu.topItems.forEach((topItem: any, index) => {
      if (this.checktargetedPreviewIds(topItem)) {
        topItems.push(
          <TopItem
            key={`topItem_${index}`}
            config={topItem.config}
            type={topItem.type}
            index={index}
            elementGroups={topItem.elementGroups}
            updateItems={() => {
              this.getTopItems();
            }}
          />,
        );
      }
    });
    if (
      this.isInEditMode &&
      !this.droppableElementGroupSpotsAreShowing &&
      !this.droppableElementSpotsAreShowing &&
      !this.droppableTopItemSpotsAreShowing
    ) {
      topItems.push(
        <div
          className={megamenuStyles.topItemAddNewButton}
          key={`topItemAddNewButtonMore_${this.navigator.megamenu.topItems.length}`}
          onClick={() => {
            if (!this.checkedOut && !this.isShowingAnalytics) {
              this.selectedTopMenuItemIndex = undefined;
              this.addTopItem();
            }
          }}
        >
          <div className={megamenuStyles.topItemAddNewButtonIcon}>
            <IAIcon
              title="Add"
              size={24}
              color={this.navigator.design.topItems.textColor}
              style={{
                marginLeft: -2,
              }}
            />
          </div>
          <div
            className={megamenuStyles.topItemAddNewButtonText}
            style={{
              color: this.navigator.design.topItems.textColor,
            }}
          >
            {this.localizationService.strings.Navigator_Megamenu_AddItem}
          </div>
        </div>,
      );
    }
    if (
      this.isInEditMode &&
      !this.droppableElementGroupSpotsAreShowing &&
      !this.droppableElementSpotsAreShowing &&
      this.navigator.megamenu.topItems.length > 1 &&
      !this.droppableTopItemSpotsAreShowing
    ) {
      topItems.push(
        <div
          className={megamenuStyles.topItemAddNewButton}
          key={`topItemAddNewButton_${this.navigator.megamenu.topItems.length}`}
          onClick={() => {
            if (!this.checkedOut && !this.isShowingAnalytics) {
              if (this.droppableTopItemSpotsAreShowing) {
                this.closeReorderTopItem();
                this.selectTopMenuItem(undefined);
              } else {
                this.reorderTopItem();
                this.selectTopMenuItem(undefined);
              }
              this.getTopItems();
            }
          }}
        >
          <div className={megamenuStyles.topItemAddNewButtonIcon}>
            <IAIcon
              title={this.droppableTopItemSpotsAreShowing ? 'Checkmark' : 'Move'}
              size={20}
              color={this.navigator.design.topItems.textColor}
            />
          </div>
          {this.navigator != undefined &&
            this.navigator.megamenu != undefined &&
            this.navigator.megamenu.topItems != undefined &&
            this.navigator.megamenu.topItems.length > 1 && (
              <div
                className={megamenuStyles.topItemAddNewButtonText}
                style={{
                  color: this.navigator.design.topItems.textColor,
                }}
              >
                {this.droppableTopItemSpotsAreShowing
                  ? this.localizationService.strings.Navigator_Megamenu_DoneReordering
                  : this.localizationService.strings.Navigator_Megamen_ReorderTopItems}
              </div>
            )}
        </div>,
      );
    }
    this.topItems = topItems;
    setTimeout(() => {
      this.resize();
    }, 0);
  }

  // Get Targeting Application information

  @action public async getTargetingApplication(): Promise<void> {
    return await TargetingService.getApplication(this.environment, this.tenant, this.token, 'Navigator', this.isAdminPage).then((response: { httpStatus: IHttpStatus, value?: ITargetingApplications[] }) => {
      this.applicationSettings = response.value[0];
      let application: IApplication;
      if (response.value && response.value.length > 0) {
        const appData = JSON.parse(response.value[0].defaultValues);
        let defaultValues: IDefaultValues;
        if (!appData || Array.isArray(appData)) {
          const list: any[] = [];
          defaultValues = {
            isEnable: false,
            type: 'profileData',
            values: list,
            allValues: false,
          };
          application = {
            id: undefined,
            title: response.value[0].title,
            name: response.value[0].title,
            instances: [],
            default: defaultValues,
          };
        } else {
          application = appData;
          if (response.value && response.value[0]) {
            application.default.type = response.value[0].applicationSettings?.toLocaleLowerCase() === 'profile data' ? 'profileData' : 'userGroups';
          }
        }

        if (application) {
          if (application.default && typeof application.default.isEnable === 'string') {
            application.default.isEnable = /true/i.test(application.default.isEnable);
          }

          if (application.instances && application.instances.length > 0) {
            application.instances.forEach((i: Instance) => {
              if (i && typeof i.isEnable === 'string') {
                const isEnabled: string = i.isEnable;
                i.isEnable = /true/i.test(isEnabled);
              }
            });
          }
        }

        this.applicationSettings.application = application;
      }
    },
    );
  }

  @observable public showSnowPopup: boolean = false;
  @observable public showConfettiPopup: boolean = false;
  @observable public showEasterConfettiPopup: boolean = false;
  @observable public showHalloweenConfettiPopup: boolean = false;

  // Feature flags

  @action public getCodeSnippetComponentIfEnabled(): { key: string, value: string }[] {
    if (this.tenantSettings?.navigator?.enableCodeSnippetComponent) {
      return Constants.SMARTMENU_TOP_ITEM_CODE_SNIPPET;
    }
    return [];
  }

  @action public getAIComponentIfEnabled(): { key: string, value: string }[] {
    return Constants.SMARTMENU_TOP_ITEM_AI;
  }

  @action public getTargetedPreviewComponentIfEnabled(): { key: string, value: string }[] {
    if (this.applicationSettings?.applicationSettings === "User groups") {
      return Constants.SMARTMENU_TOP_ITEM_TARGETED_PREVIEW;
    }
    return [];
  }
}

